import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { HelpCircle, X, Send, Sparkles, Bot } from "lucide-react";

/**
 * Floating AI Help drawer. Rendered at the app root so it's available on every route.
 * Pings /api/help/ask which is powered by Claude Sonnet 4.5 via emergentintegrations.
 */
export default function AiHelpDrawer() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hi! I'm the Credit Quack Help Assistant. Ask me anything — 'how do I upload a doc?', 'where's my funding score?', 'why does my status say Doc Issue?'" },
  ]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, open]);

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/help/ask", {
        message: q,
        history: msgs.map((m) => ({ role: m.role, text: m.text })),
      });
      setMsgs((m) => [...m, { role: "assistant", text: data.answer || "I couldn't parse that response." }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: formatApiError(e.response?.data?.detail) || "Something went wrong. Try again in a moment." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating trigger removed — Coach Ducky sits in that spot for clients.
          Drawer is still reachable programmatically if needed elsewhere. */}

      {/* Drawer */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[80vh] rounded-3xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/25 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
          data-testid="ai-help-drawer"
        >
          <div className="p-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-amber-500 flex items-center justify-center">
              <Bot size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-black text-sm">Credit Quack Assistant</p>
              <p className="text-[11px] text-slate-300 flex items-center gap-1"><Sparkles size={10} className="text-amber-300" /> Claude Sonnet 4.5 · trained on this platform</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" className="p-1.5 rounded-lg hover:bg-white/10" data-testid="ai-help-close">
              <X size={16} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-slate-50 to-white">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${i}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${m.role === "user" ? "bg-amber-500 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                  <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-white border border-slate-200 px-3.5 py-3 flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-slate-100 bg-white flex gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Ask a question…"
              disabled={busy}
              className="flex-1 h-10 px-3 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-300 disabled:opacity-60"
              data-testid="ai-help-input"
            />
            <button
              onClick={send}
              disabled={busy || !text.trim()}
              className="h-10 w-10 rounded-xl bg-slate-900 hover:bg-amber-500 text-white flex items-center justify-center disabled:opacity-50 transition"
              data-testid="ai-help-send"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
